import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { renderConverterWord } from 'utils/helperFunction'
import { PracticeWord, SpeakingModeId } from 'types/writeSpeakTypes'
import { countCharacters, formatClock, MODE_NAME } from './logic'

interface ReceiptProps {
  isTaster: boolean
  /** Pro and the free correction: the teacher also sees the words that were said. */
  wordsSaid: boolean
  writing: string
  recording: { mode: SpeakingModeId; seconds: number } | null
  words: PracticeWord[]
  usedWords: string[]
  characterPreference?: string
}

const PenIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M4 20h4L19 9l-4-4L4 16z' />
    <path d='M13.5 6.5l4 4' />
  </svg>
)

const MicIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <rect x='9' y='3' width='6' height='11' rx='3' />
    <path d='M5 11a7 7 0 0 0 14 0M12 18v3' />
  </svg>
)

/** The send area after a send: a drawn check, what went, and the words used popping in one by one. */
const Receipt: React.FC<ReceiptProps> = ({
  isTaster,
  wordsSaid,
  writing,
  recording,
  words,
  usedWords,
  characterPreference,
}) => {
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()
  const n = words.filter((w) => usedWords.indexOf(w.word) !== -1).length
  const total = words.length
  const teacherSees = wordsSaid ? t('receipt.teacherSeesWrittenAndSaid') : t('receipt.teacherSeesWritten')
  const wordsNote =
    n === total
      ? t('receipt.everyOne', { teacherSees })
      : n === 0
        ? t('receipt.tryTomorrow')
        : teacherSees
  let onIndex = 0

  return (
    <div className='ws-receipt' role='status'>
      <div className='ws-r-head'>
        <span className='ws-r-ic' aria-hidden='true'>
          <svg width='22' height='22' viewBox='0 0 22 22'>
            <path d='M5 11.5l4 4 8-9' />
          </svg>
        </span>
        <div>
          <h4>{isTaster ? t('receipt.tasterTitle') : t('receipt.title')}</h4>
          <p>{t('receipt.body')}</p>
        </div>
      </div>
      <div className='ws-r-items'>
        <div className={`ws-r-item${writing ? '' : ' off'}`}>
          <span className='ws-ic'>
            <PenIcon />
          </span>
          <div>
            <b>{t('receipt.yourWriting')}</b>
            <span>
              {writing ? t('receipt.characters', { count: countCharacters(writing) }) : t('receipt.notIncluded')}
            </span>
          </div>
        </div>
        <div className={`ws-r-item${recording ? '' : ' off'}`}>
          <span className='ws-ic'>
            <MicIcon />
          </span>
          <div>
            <b>{t('receipt.yourRecording')}</b>
            <span>
              {recording
                ? t('receipt.recordingSummary', { mode: t(MODE_NAME[recording.mode]), time: formatClock(recording.seconds) })
                : t('receipt.notIncluded')}
            </span>
          </div>
        </div>
      </div>
      {total > 0 && (
        <div className='ws-r-words'>
          <div className='ws-r-wtitle'>
            {n === total ? (
              t('receipt.allUsed', { count: n })
            ) : n === 0 ? (
              t('receipt.noneUsed')
            ) : (
              <Trans t={t} i18nKey='receipt.someUsed' values={{ used: n, total }} components={{ b: <b /> }} />
            )}
          </div>
          <div className='ws-r-wlist'>
            {words.map((w) => {
              const on = usedWords.indexOf(w.word) !== -1
              const delay = on ? 250 + onIndex++ * 90 : 0
              return (
                <span
                  key={w.word}
                  className={`ws-r-chip${on ? ' on' : ''}`}
                  style={{ animationDelay: `${delay}ms` }}
                  lang={zhLang}
                >
                  {on && (
                    <svg viewBox='0 0 12 12' aria-hidden='true'>
                      <path d='M2.5 6.2l2.3 2.3 4.7-4.9' />
                    </svg>
                  )}
                  {renderConverterWord(w.word, characterPreference || '')}
                  <span className='ws-sr'>{on ? t('receipt.used') : t('receipt.notUsed')}</span>
                </span>
              )
            })}
          </div>
          <p>{wordsNote}</p>
        </div>
      )}
    </div>
  )
}

export default Receipt
